import React, { useEffect } from "react";

import useCurrentUser from "@editor/hooks/useCurrentUser";
import { useLogAnalytics } from "@editor/hooks/useLogAnalytics";
import usePendingShopifyIntegrationCookie from "@editor/hooks/usePendingShopifyIntegrationCookie";
import { isFeatureEnabled } from "@editor/infra/featureFlags";
import { trpc, trpcUtils } from "@editor/utils/trpc";
import { ReploIconBlack } from "@svg/replo-icon-black";

import { useNavigate, useParams } from "react-router-dom";
import { useEffectEvent } from "replo-utils/react/use-effect-event";

import BasicShopInfo from "./BasicShopInfo";
import ConnectYourStore from "./ConnectYourStore";

export interface OnboardingStepProps {
  currentStep: number;
  submitStep: () => void;
  isLoading: boolean;
}

// NOTE (Cole, 2025-04-16): This is the core structure of the onboarding flow - if you want to add a new step, just put it here. Order matters. Every step should handle all its own state and simply call the submitStep function when it's ready to move to the next step.
const onboardingSteps = [
  {
    slug: "setup",
    component: ConnectYourStore,
  },
  {
    slug: "workspace-details",
    component: BasicShopInfo,
  },
] as const;

const OnboardingLayout = ({ children }: { children: React.ReactNode }) => {
  return (
    <div className="bg-neutral-soft flex h-screen w-full items-stretch justify-center md:p-6">
      <div className="md:border-border shadow-button-selectable relative h-full w-full overflow-hidden border bg-white md:rounded-[24px]">
        <div className="absolute left-4 top-4 hidden md:left-8 md:top-8 md:block">
          <ReploIconBlack className="h-6 w-6 md:h-8 md:w-8" />
        </div>
        {children}
      </div>
    </div>
  );
};

const Onboarding: React.FC<React.PropsWithChildren> = ({ children }) => {
  // NOTE (Ben O., 2025-05-11): This is a temporary state to prevent the user from being redirected to the home page when they complete the onboarding flow
  const [justCompletedOnboarding, setJustCompletedOnboarding] =
    React.useState(false);

  const { onboardingStep } = useParams<{ onboardingStep?: string }>();
  const currentStepIndex = onboardingSteps.findIndex(
    (step) => step.slug === onboardingStep,
  );

  const { user } = useCurrentUser();
  const logEvent = useLogAnalytics();
  const navigate = useNavigate();
  const { pendingShopifyIntegrationCookie } =
    usePendingShopifyIntegrationCookie();

  const { mutate: updateStatusMutation, isPending: isLoading } =
    trpc.onboarding.updateOnboardingStatus.useMutation();
  const { mutate: setUpInitialWorkspaceAndProject } =
    trpc.onboarding.setUpInitialWorkspaceAndProject.useMutation();

  const isUsingNewOnboarding = isFeatureEnabled("new-onboarding");

  const onBeforeUnload = useEffectEvent(() => {
    if (onboardingStep) {
      // If there's a pending Shopify integration cookie, the user is being redirected to Shopify
      // and not actually abandoning the flow
      const isUserAbandoningFlow =
        onboardingStep === "setup"
          ? !Boolean(pendingShopifyIntegrationCookie)
          : true;

      if (isUserAbandoningFlow) {
        logEvent("onboarding.flow.abandoned", {
          lastStepCompleted: onboardingStep,
          stepsData: {},
          whatToDo: [],
        });
      }
    }
  });

  useEffect(() => {
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", onBeforeUnload);
    };
  }, [onBeforeUnload]);

  // TODO (Cole, 2025-04-17): Remove this when we remove the feature flag
  useEffect(() => {
    if (!isUsingNewOnboarding) {
      navigate("/");
    }
  }, [navigate, isUsingNewOnboarding]);

  useEffect(() => {
    if (
      !justCompletedOnboarding &&
      user?.isOnboardingCompleted &&
      user.onboardingCompletedAt
    ) {
      navigate("/home");
    }
  }, [
    navigate,
    user?.isOnboardingCompleted,
    justCompletedOnboarding,
    user?.onboardingCompletedAt,
  ]);

  // NOTE (Cole, 2025-04-17): Navigate to step 1 if given step is not in mapping
  useEffect(() => {
    const isValidStep =
      onboardingStep &&
      onboardingSteps.some((step) => step.slug === onboardingStep);

    if (!isValidStep) {
      // Navigate to the first step's slug if the current step is invalid
      navigate(`/onboarding/${onboardingSteps[0].slug}`, {
        replace: true,
      });
    }
  }, [onboardingStep, navigate]);

  const submitStep = () => {
    if (currentStepIndex === -1) {
      navigate(`/onboarding/${onboardingSteps[0].slug}`, {
        replace: true,
      });
      return;
    }

    const isLastStep = currentStepIndex === onboardingSteps.length - 1;

    if (isLastStep) {
      updateStatusMutation(
        { isOnboardingCompleted: true, onboardingCompletedAt: new Date() },
        {
          onSuccess: ({ project }) => {
            setJustCompletedOnboarding(true);
            // NOTE (Ben O., 2025-05-13): Kinda redundant, but we need to call this to ensure the user's workspace is created
            // in scenarios where the user started the old onboarding flow when we didn't initially create a workspace for them
            setUpInitialWorkspaceAndProject();
            void trpcUtils.user.get.invalidate().then(() => {
              logEvent("onboarding.flow.completed", {
                stepsData: { projectId: project?.id },
                whatToDo: [],
              });

              localStorage.removeItem("onboarding-firstName");
              localStorage.removeItem("onboarding-lastName");
              localStorage.removeItem("onboarding-shopName");
              localStorage.removeItem("onboarding-teamDescription");
              localStorage.removeItem("onboarding-annualRevenue");
              localStorage.removeItem("onboarding-setup-selected-option");

              navigate(`/editor/${project?.id}/new`);
            });
          },
          onError: (error) => {
            console.error("Failed to update onboarding status:", error);
          },
        },
      );
    } else {
      const nextStep = onboardingSteps[currentStepIndex + 1];
      if (nextStep) {
        navigate(`/onboarding/${nextStep.slug}`);
      }
    }
  };

  const currentStepDetails = onboardingStep
    ? onboardingSteps.find((step) => step.slug === onboardingStep)
    : null;

  const StepComponent = currentStepDetails
    ? currentStepDetails.component
    : null;

  if (!StepComponent) {
    return null;
  }

  return (
    <OnboardingLayout>
      <StepComponent
        currentStep={currentStepIndex + 1}
        submitStep={submitStep}
        isLoading={isLoading}
      />
      {children}
    </OnboardingLayout>
  );
};

export default Onboarding;
